import logger from '../../../utils/logger';
/**
 * Validation helpers for impersonation functionality
 */

/**
 * Validates if a user record can be impersonated
 * @param {Object} record - User record from React Admin
 * @returns {Object} - { isValid: boolean, reason: string }
 */
export const validateImpersonationEligibility = (record) => {
    if (!record) {
        return { isValid: false, reason: 'No user record provided' };
    }

    if (!record.isPatient) {
        return { isValid: false, reason: 'Only patients can be impersonated' };
    }

    if (!record.healthie_id) {
        return { isValid: false, reason: 'Patient missing Healthie ID' };
    }

    if (record.is_deleted || !record.is_active) {
        return { isValid: false, reason: 'Cannot impersonate inactive or deleted patients' };
    }

    return { isValid: true, reason: null };
};

/**
 * Formats the impersonation request payload
 * @param {Object} record - User record
 * @param {string} reason - Optional reason for impersonation
 * @returns {Object} - Formatted request payload
 */
export const formatImpersonationRequest = (record, reason = '') => {
    return {
        patient_user_id: record.id,
        reason: reason.trim()
    };
};

/**
 * Formats error messages for user display
 * @param {Error|Object} error - Error object or API response
 * @returns {string} - User-friendly error message
 */
export const formatImpersonationError = (error) => {
    if (typeof error === 'string') {
        return error;
    }

    if (error?.response?.data?.message) {
        return error.response.data.message;
    }

    if (error?.message) {
        return error.message;
    }

    if (error?.reason) {
        return error.reason;
    }

    return 'Failed to create impersonation session. Please try again.';
};

/**
 * Validates the deep link format
 * @param {string} deepLink - The deep link returned from the API
 * @returns {boolean} - Whether the deep link is valid
 */
export const validateDeepLink = (deepLink) => {
    if (!deepLink || typeof deepLink !== 'string') {
        return false;
    }

    // Validate the deep link format with proper regex pattern
    // Expected format: pacify://impersonate?code=<exactly 32-character hexadecimal code>
    const deepLinkPattern = /^pacify:\/\/impersonate\?code=[a-f0-9]{32}$/;
    return deepLinkPattern.test(deepLink);
};

/**
 * Gets the patient display information for UI
 * @param {Object} record - User record
 * @returns {Object} - Display information
 */
export const getPatientDisplayInfo = (record) => {
    return {
        first_name: record.firstName || record.first_name || 'Unknown',
        last_name: record.lastName || record.last_name || 'User',
        email: record.email || 'No email',
        healthie_id: record.healthie_id
    };
};

/**
 * Creates a web link for impersonation testing
 * @param {string} deepLink - The deep link from the API response
 * @returns {string|null} - Web URL for browser testing or null if not possible
 */
export const createImpersonationWebLink = (deepLink) => {
    try {
        // Debug logging
        logger.log('🔍 createImpersonationWebLink called with deepLink:', deepLink);
        logger.log('🔍 deepLink type:', typeof deepLink);
        logger.log('🔍 deepLink length:', deepLink?.length);
        
        // Check if deepLink is valid
        if (!deepLink || typeof deepLink !== 'string' || deepLink.trim() === '') {
            logger.warn('Invalid or empty deepLink provided:', deepLink);
            return null;
        }
        
        // Extract impersonation code from deep link (pacify://impersonate?code=XXX)
        const url = new URL(deepLink);
        const code = url.searchParams.get('code');
        
        if (!code) {
            logger.warn('No impersonation code found in deep link:', deepLink);
            return null;
        }
        
        // Get PWA base URL from environment
        const pwaBaseUrl = import.meta.env.VITE_PATIENT_PWA_URL;
        if (!pwaBaseUrl) {
            logger.warn('VITE_PATIENT_PWA_URL environment variable not set');
            return null;
        }
        
        // Create web link
        return `${pwaBaseUrl}impersonate?code=${code}`;
    } catch (error) {
        logger.error('Failed to create impersonation web link:', error);
        return null;
    }
}; 